import React, { useEffect, useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { CourtRuleDeadline } from '../../types/playbook.types';

interface CourtRulesBrowserProps {
  onSelectDeadline: (deadline: CourtRuleDeadline) => void;
  onClose: () => void;
}

const CourtRulesBrowser: React.FC<CourtRulesBrowserProps> = ({ onSelectDeadline, onClose }) => {
  const [deadlines, setDeadlines] = useState<CourtRuleDeadline[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [jurisdiction, setJurisdiction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await playbookApi.listCourtRuleDeadlines({
        jurisdiction_code: jurisdiction || undefined,
        search: search || undefined,
      });
      const data = (resp as any)?.deadlines ?? (resp as any)?.data?.deadlines ?? [];
      setDeadlines(Array.isArray(data) ? data : []);
    } catch {
      setDeadlines([]);
    } finally {
      setLoading(false);
    }
  }, [search, jurisdiction]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <div className="border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 w-72 flex flex-col" style={{ animation: 'fadeSlideLeft 200ms ease-out' }}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Court Rules</h4>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm">×</button>
      </div>

      <div className="px-3 py-2 space-y-1.5 border-b border-gray-100 dark:border-gray-700">
        <input
          type="text"
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by statute..."
        />
        <input
          type="text"
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder="Jurisdiction code (e.g. CA)"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-6">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
          </div>
        )}

        {!loading && deadlines.length === 0 && (
          <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center py-6">No deadlines found</p>
        )}

        {deadlines.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => onSelectDeadline(d)}
            className="w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 hover:bg-lex-state-hover transition-colors"
          >
            <div className="text-[10px] font-medium text-gray-700 dark:text-gray-300">{d.statute_reference}</div>
            {d.description && (
              <div className="text-[9px] text-gray-400 dark:text-gray-500 truncate">{d.description}</div>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[8px] text-lex-accent">{d.days} {d.day_type} days {d.direction}</span>
              {d.jurisdiction_code && (
                <span className="text-[8px] text-gray-400">{d.jurisdiction_code}</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CourtRulesBrowser;
